import type { DefLookup } from "../catalog";
import { CONTROL_CIRCUITS } from "../catalog/control-circuits";
import type { ComponentDefinition, System } from "../types";
import { matchesCircuitDefinition } from "./circuit-component-utils";

const cache = new WeakMap<System, WeakMap<DefLookup, System[]>>();
const fixedScenarios = new WeakSet<System>();
export const CONTROL_DESIGN_STATE_LIMIT = 64;

function controlSelections(system: System, lookup: DefLookup) {
  return system.nodes.flatMap(node => {
    if (node.kind !== "component") return [];
    const def = lookup(node.definitionId, node.customDefinition);
    const baseline = CONTROL_CIRCUITS.find(part => part.id === def?.id);
    if (!def || !baseline || !matchesCircuitDefinition(def, baseline)) return [];
    const options = def.simulateStates!.options.filter(option => option.intraLinks?.length);
    return [{ nodeId: node.id, def, options }];
  });
}

export function controlDesignStatesTruncated(system: System, lookup: DefLookup): boolean {
  return controlSelections(system, lookup).reduce((n, control) => n * control.options.length, 1) > CONTROL_DESIGN_STATE_LIMIT;
}

/** This is a diagnostic-only, single-position snapshot, never a persistent
 * declaration that the whole installation has a verified design envelope. */
export function isFixedControlDesignScenario(system: System): boolean {
  return fixedScenarios.has(system);
}

export function fixedControlDesignScenario(system: System, selections: ReadonlyMap<string, ComponentDefinition>): System {
  const result: System = {
    ...system,
    nodes: system.nodes.map(node => {
      const def = selections.get(node.id);
      return node.kind === "component" && def
        ? { ...node, definitionId: "custom", customDefinition: def }
        : node;
    }),
  };
  fixedScenarios.add(result);
  return result;
}

/** Separate design scenarios, never a union of mutually exclusive contacts.
 * Keep other equipment's ordinary design behavior (including converters) intact.
 * A bounded enumeration is not evidence that every installation state is sized;
 * control diagnostics retain the explicit per-position verification notice. */
export function controlDesignSystems(system: System, lookup: DefLookup): System[] {
  let byLookup = cache.get(system);
  if (!byLookup) { byLookup = new WeakMap(); cache.set(system, byLookup); }
  const cached = byLookup.get(lookup);
  if (cached) return cached;
  const controls = controlSelections(system, lookup);
  if (!controls.length || controls.reduce((n, c) => n * c.options.length, 1) > CONTROL_DESIGN_STATE_LIMIT) {
    byLookup.set(lookup, []);
    return [];
  }
  let selections: Map<string, ComponentDefinition>[] = [new Map()];
  for (const { nodeId, def, options } of controls) {
    selections = selections.flatMap(selection => options.map(option =>
      new Map(selection).set(nodeId, {
        ...def, simulateStates: { ...def.simulateStates!, default: option.id },
      })));
  }
  const result = selections.map(selection => fixedControlDesignScenario(system, selection));
  byLookup.set(lookup, result);
  return result;
}
